import React, { useEffect, useState } from "react";
import { Loader2, Search, Ship, Eye, Download, FileText, PackageCheck } from "lucide-react";
import api, { formatApiError, money, openPdf } from "../lib/api";
import { smartSearch } from "../lib/search";
import { useSortable } from "../lib/sortable";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";

const landedOf = (d) => Number(d.cost_total || 0) + Number(d.extra_doc || 0);
const landedGtqOf = (d) => landedOf(d) * Number(d.exchange_rate || 1);
const fobUnit = (it) => Number(it.unit_effective ?? it.unit_cost ?? 0);

export default function Imports() {
  const { can } = useAuth();
  const { Th, sortRows } = useSortable();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiving, setReceiving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/documents?doc_type=importacion")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (row) => {
    setPdfLoading(true);
    try { await openPdf(`/documents/${row.id}/costing/pdf`); }
    catch { toast.error("No se pudo generar el PDF del costeo"); }
    finally { setPdfLoading(false); }
  };

  const doReceive = async () => {
    setReceiving(true);
    try {
      const r = await api.post(`/documents/${receiveTarget.id}/receive-inventory`);
      const nrec = r.data.received?.length || 0;
      const nnf = r.data.not_found?.length || 0;
      toast.success(`${nrec} producto(s) recibido(s) en inventario con su costo en GTQ`);
      if (nnf > 0) toast.warning(`${nnf} línea(s) sin producto en catálogo: ${r.data.not_found.map((x) => x.code).join(", ")}`);
      setReceiveTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReceiving(false); }
  };

  const filtered = smartSearch(rows, q, ["number", "party_name", "doc_number", "status"]);

  return (
    <div className="space-y-5">
      <div className="rounded-sm border border-border bg-muted/40 px-4 py-3 flex items-start gap-3">
        <FileText className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Las importaciones se registran en el <strong className="text-foreground">Registro Diario</strong> (tipo <em>Importación</em>). Aquí visualizas el <strong className="text-foreground">costeo</strong>: FOB, gastos prorrateados y el costo unitario aterrizado de cada línea.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-80"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="imp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por N°, proveedor, referencia..." className="pl-9 rounded-sm" /></div>
      </div>

      <Card className="rounded-sm border shadow-none overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
          filtered.length === 0 ? <div className="flex flex-col items-center py-16 text-muted-foreground"><Ship className="h-8 w-8 mb-2" /><p className="text-sm">Sin importaciones registradas.</p><p className="text-xs mt-1">Regístralas desde el Registro Diario con tipo "Importación".</p></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="imp-table">
                <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <Th field="number">N° Registro</Th><Th field="party_name">Proveedor</Th><Th field="doc_number">Ref. / Doc.</Th>
                  <Th field="issue_date">Fecha</Th><th className="px-4 py-3 font-bold text-right">Costo aterrizado</th><Th field="inventory_received">Inventario</Th><th className="px-4 py-3 font-bold text-right">Acciones</th></tr></thead>
                <tbody>
                  {sortRows(filtered).map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/50" data-testid={`imp-row-${r.number}`}>
                      <td className="px-4 py-2.5 font-mono text-xs font-bold text-primary">{r.number}</td>
                      <td className="px-4 py-2.5 font-medium">{r.party_name || "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{r.doc_number || "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{r.issue_date || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">
                        <div>{money("GTQ", landedGtqOf(r))}</div>
                        {r.currency === "USD" && <div className="text-[10px] text-muted-foreground font-normal">{money(r.currency, landedOf(r))}</div>}
                      </td>
                      <td className="px-4 py-2.5">
                        {r.inventory_received
                          ? <Badge className="rounded-sm font-semibold bg-green-100 text-green-700 gap-1"><PackageCheck className="h-3 w-3" /> En inventario</Badge>
                          : <Badge className="rounded-sm font-semibold bg-slate-100 text-slate-600">Pendiente</Badge>}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Ver costeo" onClick={() => setDetail(r)} data-testid={`imp-detail-${r.number}`}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Descargar PDF" onClick={() => downloadPdf(r)} data-testid={`imp-pdf-${r.number}`}><Download className="h-4 w-4" /></Button>
                        {can("inventory:adjust") && !r.inventory_received && r.status !== "Anulado" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" title="Recibir en inventario" onClick={() => setReceiveTarget(r)} data-testid={`imp-receive-${r.number}`}><PackageCheck className="h-4 w-4" /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="imp-detail-dialog">
          {detail && (<>
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="font-heading">Costeo · {detail.number}</DialogTitle>
                  <DialogDescription>{detail.party_name} · {detail.issue_date} · {detail.currency} · TC {detail.exchange_rate}</DialogDescription>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {detail.inventory_received && <Badge className="rounded-sm font-semibold bg-green-100 text-green-700 gap-1"><PackageCheck className="h-3 w-3" /> En inventario</Badge>}
                  {can("inventory:adjust") && !detail.inventory_received && detail.status !== "Anulado" && (
                    <Button variant="outline" className="rounded-sm gap-2 text-xs text-green-600 border-green-200" onClick={() => setReceiveTarget(detail)} data-testid="imp-detail-receive"><PackageCheck className="h-4 w-4" /> Recibir</Button>
                  )}
                  <Button variant="outline" className="rounded-sm gap-2 text-xs" disabled={pdfLoading} onClick={() => downloadPdf(detail)} data-testid="imp-detail-pdf">
                    {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF costeo
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <div className="border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid="imp-costing-table">
                <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground">
                  <th className="px-2 py-2">Código</th><th className="px-2 py-2">Descripción</th><th className="px-2 py-2 text-right">Cant.</th>
                  <th className="px-2 py-2 text-right">FOB unit</th><th className="px-2 py-2 text-right">FOB total</th>
                  <th className="px-2 py-2 text-right">Prorrateo</th><th className="px-2 py-2 text-right">Aterrizado</th><th className="px-2 py-2 text-right">Unit. aterrizado</th><th className="px-2 py-2 text-right bg-primary/10 text-primary">Costo unit. (GTQ)</th></tr></thead>
                <tbody>
                  {detail.items.map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1.5 font-mono text-xs">{it.code || "—"}</td>
                      <td className="px-2 py-1.5">{it.description}</td>
                      <td className="px-2 py-1.5 text-right">{it.qty}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{money(detail.currency, fobUnit(it))}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{money(detail.currency, it.cost_total)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-amber-600">{money(detail.currency, it.prorated_expense)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{money(detail.currency, it.landed_total)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{money(detail.currency, it.landed_unit_cost)}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold text-primary bg-primary/5">{money("GTQ", Number(it.landed_unit_cost || 0) * Number(detail.exchange_rate || 1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(detail.extra_costs || []).length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Gastos de importación</p>
                <div className="border border-border rounded-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground"><th className="px-2 py-2">Concepto</th><th className="px-2 py-2">Proveedor</th><th className="px-2 py-2">Moneda</th><th className="px-2 py-2 text-right">Monto</th></tr></thead>
                    <tbody>
                      {detail.extra_costs.map((e, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1.5">{e.concept_other || e.concept || "Gasto"}</td>
                          <td className="px-2 py-1.5 text-xs">{e.supplier_name || "—"}</td>
                          <td className="px-2 py-1.5 text-xs">{e.currency || "GTQ"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{money(e.currency || "GTQ", e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <div className="w-72 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">FOB total</span><span className="font-mono">{money(detail.currency, detail.cost_total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Gastos prorrateados</span><span className="font-mono text-amber-600">{money(detail.currency, detail.extra_doc)}</span></div>
                {detail.currency === "USD" && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Aterrizado ({detail.currency})</span><span className="font-mono">{money(detail.currency, landedOf(detail))}</span></div>
                )}
                <div className="flex justify-between border-t border-border pt-1.5 mt-1 font-bold text-primary text-base"><span>COSTO TOTAL (GTQ)</span><span className="font-mono" data-testid="imp-landed-total">{money("GTQ", landedGtqOf(detail))}</span></div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!receiveTarget} onOpenChange={(o) => !o && setReceiveTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Recibir en inventario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se sumará el stock de los productos de <strong>{receiveTarget?.number}</strong> y se actualizará su <strong>costo en quetzales (GTQ)</strong> con el costo unitario aterrizado. Los productos se emparejan por código; las líneas sin producto en catálogo se omitirán. Esta acción no se puede deshacer desde aquí.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={receiving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-green-600 hover:bg-green-700" disabled={receiving} onClick={(e) => { e.preventDefault(); doReceive(); }} data-testid="confirm-receive-imp">
              {receiving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recibir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
